import { api } from "./api.service";

// Tipos usados na busca de médicos para vincular à secretaria.
// O backend (/doctors/search) retorna dados do modelo Doctor + user associado.
interface DoctorSearchApiResult {
  doctorId: string;
  CRM?: string | null;
  RQE?: string | null;
  specialty?: string | null;
  locals?: NextAvailabilityLocal[]; // locais de atendimento do médico (quando retornados pela API)
  user: {
    id: string;
    name: string;
    email: string;
    // Opcionalmente, o backend pode incluir profile.registrationNumber no futuro.
    profile?: {
      registrationNumber?: string | null;
    } | null;
  };
}

// Tipo simplificado usado pelo frontend.
// doctorId é o identificador usado pelo backend para vincular na tabela Secretary.doctors.
export interface DoctorSearchResult {
  doctorId: string;
  userId: string;
  name: string;
  email: string;
  crm?: string | null;
  cpf?: string | null;
  locals?: NextAvailabilityLocal[]; // opcionais: locais associados a este médico
}

export interface NextAvailabilityLocal {
  id: string;
  localName?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  phone?: string | null;
}

export interface NextAvailabilityResult {
  doctorId: string;
  doctorName?: string | null;
  scheduleId: string | null;
  localId: string | null;
  local?: NextAvailabilityLocal | null;
  slotStart: string | null; // ISO
  slotEnd: string | null;   // ISO
}

/**
 * Busca médicos por CPF, CRM ou nome.
 *
 * Endpoint: GET /doctors/search?q=termo
 */
export async function searchDoctors(query: string): Promise<DoctorSearchResult[]> {
  const trimmed = query.trim();
  if (!trimmed) return [];

  const { data } = await api.get<DoctorSearchApiResult[]>("/doctors/search", {
    params: { q: trimmed },
  });

  const list = Array.isArray(data) ? data : [];

  // Mapeia o retorno bruto da API (Doctor + user) para um tipo simplificado.
  return list.map((item) => ({
    doctorId: item.doctorId,
    userId: item.user.id,
    name: item.user.name,
    email: item.user.email,
    crm: item.CRM ?? null,
    // Se no futuro o backend incluir o CPF no select (profile.registrationNumber), usamos aqui.
    cpf: item.user.profile?.registrationNumber ?? null,
    locals: item.locals ?? [],
  }));
}

/**
 * Cria um novo intervalo de agenda (Schedule) para um médico e local específicos.
 *
 * Agora o backend aceita (e pode exigir) os identificadores do médico e do local,
 * além de um status opcional do schedule.
 */
export interface CreateDoctorSchedulePayload {
  // Data e hora de início do horário disponível (ISO string)
  startDate: string;

  // Data e hora de fim do horário disponível (ISO string)
  endDate: string;

  // Médico que terá este horário disponível (obrigatório)
  doctorId: string;

  // Local (clínica/consultório) onde o médico atenderá neste horário (obrigatório)
  localId: string;

  // Status opcional do schedule (ex.: "ATIVO"). Se não enviado, será definido um padrão no service.
  status?: string;
}

export async function createDoctorSchedule({
  startDate,
  endDate,
  doctorId,
  localId,
  status,
}: CreateDoctorSchedulePayload) {
  const { data } = await api.post("/doctors/schedules", {
    startDate,
    endDate,
    doctorId,
    localId,
    ...(status ? { status } : {}),
  });
  return data;
}

export interface RemoveDoctorScheduleSlotPayload {
  scheduleId: string;
  slotStart: string; // ISO
  slotEnd: string;   // ISO
}

export async function removeDoctorScheduleSlot(payload: RemoveDoctorScheduleSlotPayload) {
  const { data } = await api.post("/doctors/schedules/remove-slot", payload);
  return data;
}

export async function fetchNextAvailability(params?: { slotMinutes?: number; localId?: string; after?: string; doctorId?: string }): Promise<NextAvailabilityResult | null> {
  const { slotMinutes, localId, after, doctorId } = params || {};
  const path = doctorId ? "/doctors/next-availability/by" : "/doctors/next-availability";
  try {
    const { data } = await api.get(path, {
      params: {
        ...(slotMinutes ? { slotMinutes } : {}),
        ...(localId ? { localId } : {}),
        ...(after ? { after } : {}),
        ...(doctorId ? { doctorId } : {}),
      },
    });
    return data ?? null;
  } catch (e: any) {
    const status = e?.response?.status;
    if (status === 404) {
      // Sem horários disponíveis
      return null;
    }
    throw e;
  }
}

export async function appointTime(scheduleId:string ,patientId:string ,slotMinutes?: number, slotTime?: string ): Promise<NextAvailabilityResult | ''> {
  
  const { data } = await api.post("/appointments/from-schedule", {
    scheduleId:scheduleId,
    patientId:patientId
  });
  return data ?? null;
}

// Cria ou atualiza o local (clínica) de um médico específico.
export interface UpsertDoctorLocalPayload {
  doctorId: string;
  localId?: string;
  localName?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  phone?: string;
}

export async function upsertDoctorLocal(payload: UpsertDoctorLocalPayload) {
  const { data } = await api.post("/doctors/upsert-local", payload);
  return data;
}

export interface UpsertMyDoctorProfilePayload {
  name?: string;
  email?: string;
  crm?: string;
  rqe?: string;
  specialty?: string;
  profile?: {
    address?: string;
    city?: string;
    state?: string;
    zip_code?: string;
    birthDay?: string;
    registrationNumber?: string;
    phone?: string;
  };
  locals?: Array<{
    id?: string;
    localName?: string;
    address?: string;
    city?: string;
    state?: string;
    zip_code?: string;
    phone?: string;
  }>;
}

export async function upsertMyDoctorProfile(payload: UpsertMyDoctorProfilePayload) {
  const { data } = await api.post("/doctors/my-profile", payload);
  return data;
}

// ---------------- Agenda do médico (schedules + appointments por dia) ----------------
export interface DoctorScheduleAppointment {
  id: string;
  startTime: string; // ISO
  endTime: string;   // ISO
  status: string;
  patient: {
    id: string;
    name: string;
  } | null;
}

export interface DoctorScheduleWithAppointments {
  scheduleId: string;
  startDate: string; // ISO
  endDate: string;   // ISO
  status: string;
  local: NextAvailabilityLocal | null;
  appointments: DoctorScheduleAppointment[];
}

export interface MySchedulesByDateResponse {
  doctorId: string;
  doctorName: string | null;
  date: string; // yyyy-mm-dd
  schedules: DoctorScheduleWithAppointments[];
}

// Normaliza o retorno da API para sempre entregar um array de schedules
// "dateStr" deve ser sempre uma data local no formato "YYYY-MM-DD".
export async function getMySchedulesByDate(dateStr: string): Promise<DoctorScheduleWithAppointments[]> {
  // backend espera ?date=yyyy-mm-dd (sem conversão de timezone aqui)
  const { data } = await api.get("/doctors/my-schedules-by-date", {
    params: { date: dateStr },
  });

  // Caso antigo: { doctorId, doctorName, date, schedules: [...] }
  if (data && Array.isArray((data as any).schedules)) {
    return (data as MySchedulesByDateResponse).schedules;
  }

  // Caso novo: array direto de schedules
  if (Array.isArray(data)) {
    return data as DoctorScheduleWithAppointments[];
  }

  // Caso inesperado: sem horários
  return [];
}

// Mesma lógica de normalização que getMySchedulesByDate, mas para a rota de secretária
// que carrega a agenda de um médico específico vinculado à secretária.
export async function getDoctorSchedulesByDate(
  doctorId: string,
  dateStr: string,
): Promise<DoctorScheduleWithAppointments[]> {
  const { data } = await api.get("/doctors/schedules-by-date-for-doctor", {
    params: { doctorId, date: dateStr },
  });

  if (data && Array.isArray((data as any).schedules)) {
    return (data as MySchedulesByDateResponse).schedules;
  }

  if (Array.isArray(data)) {
    return data as DoctorScheduleWithAppointments[];
  }

  return [];
}

// Lista médicos vinculados à secretária logada.
export async function getMyLinkedDoctors(): Promise<DoctorSearchResult[]> {
  const { data } = await api.get<DoctorSearchApiResult[]>("/doctors/my-linked-doctors");
  const list = Array.isArray(data) ? data : [];

  return list.map((item) => ({
    doctorId: item.doctorId,
    userId: item.user.id,
    name: item.user.name,
    email: item.user.email,
    crm: item.CRM ?? null,
    cpf: item.user.profile?.registrationNumber ?? null,
    locals: item.locals ?? [],
  }));
}
